import { PlusIcon, UpdateIcon } from '@radix-ui/react-icons';
import { Badge, Box, Button, Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTableHoC } from 'components/common/table';
import { MachineServiceLogEditor } from 'components/sections/admin-portal/machines/service-logs/editor-dialog';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { TeamsContext } from 'contexts/admin/teams.context';
import { lightFormat, parseISO } from 'date-fns';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import {
  ServiceCategory,
  shouldCategoryResetTraining,
} from 'lib_ts/enums/machine-service-logs.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { IMachineServiceLog } from 'lib_ts/interfaces/i-machine-service-log';
import { max } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { MachineServiceLogsService } from 'services/machine-service-logs.service';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'MachineServiceLogs';

interface IProps {
  machine: IMachine;

  // e.g. default to installation from installer
  defaultCategory?: ServiceCategory;
}

export const MachineServiceLogs = (props: IProps) => {
  const current = useAuthStore((s) => s.current);

  const { teams } = useContext(TeamsContext);

  // load the logs on mount
  useEffect(() => {
    MachineServiceLogsService.getInstance()
      .getLatest(props.machine.machineID)
      .then((logs) => {
        if (!logs) {
          NotifyHelper.error({
            message_md: `Failed to load service logs for ${props.machine.machineID}.`,
          });
          return;
        }

        setServiceLogs(logs);
      });
  }, []);

  const serviceLogs = useMachinesPortalStore((s) => s.serviceLogs);
  const setServiceLogs = useMachinesPortalStore((s) => s.setServiceLogs);
  const updateMachines = useMachinesPortalStore((s) => s.updateMachines);

  const [dialog, setDialog] = useState<{
    key: number;
    model: Partial<IMachineServiceLog>;
  }>();

  const columns = useMemo<ITypedTableColumn<IMachineServiceLog>[]>(
    () => [
      {
        key: 'serviced',
        dataType: 'datetime',
        label: 'Service Date',
      },
      {
        key: 'servicedBy',
        label: 'Serviced By',
      },
      {
        key: 'category',
        label: 'Category',
      },
      {
        key: 'trainingReset',
        label: 'Training Status',
        tooltipFn: (m) =>
          m.trainingReset
            ? `Pitches trained prior to ${lightFormat(
                parseISO(m.serviced),
                'yyyy-MM-dd'
              )} will need to be trained again`
            : undefined,
        formatFn: (m) =>
          m.trainingReset ? (
            <Badge color={RADIX.COLOR.WARNING}>Reset</Badge>
          ) : undefined,
      },
      {
        key: '_description',
        label: 'Description / Notes',
        formatFn: (m) => {
          return (
            <Flex direction="column" gap="1" pt="2" pb="2">
              {m.category === ServiceCategory.BallReplacement && (
                <Box>
                  <Badge color={RADIX.COLOR.INFO}>
                    {m.ball_count} {m.ball_type} balls
                  </Badge>
                </Box>
              )}

              {m.category === ServiceCategory.Installation && (
                <Box>
                  <Badge
                    color={RADIX.COLOR.DANGER}
                    title={teams.find((t) => t._id === m.teamID)?.name}
                  >
                    {m.location}
                  </Badge>
                </Box>
              )}

              {m.notes && <Text>{m.notes}</Text>}
            </Flex>
          );
        },
      },
    ],
    [teams]
  );

  const clickable: ITableSelectable = {
    afterChangeSelected: (model: IMachineServiceLog) =>
      setDialog({
        key: Date.now(),
        model: model,
      }),
    enableSelect: true,
  };

  const sort: ITableSortable = {
    defaultSort: {
      key: 'serviced',
      dir: 1,
    },
    enableSort: true,
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <Flex direction="column" gap="4">
        <Flex justify="end">
          <Button
            color={RADIX.COLOR.SUCCESS}
            onClick={() => {
              const safeCategory =
                props.defaultCategory ?? ServiceCategory.Other;

              setDialog({
                key: Date.now(),
                model: {
                  machineID: props.machine.machineID,
                  serviced: new Date().toISOString(),
                  servicedBy: current.email,
                  category: safeCategory,
                  trainingReset: shouldCategoryResetTraining(safeCategory),
                  location: props.machine.nickname,
                  teamID: props.machine._parent_id,
                  notes: '',
                },
              });
            }}
          >
            <PlusIcon /> Create
          </Button>
        </Flex>

        <CommonTableHoC
          dataTestID="LatestServiceLogs"
          identifier="LatestServiceLogsTable"
          displayColumns={columns}
          displayData={serviceLogs}
          scrollHeight="400px"
          skipResetSelected
          {...clickable}
          {...sort}
        />
      </Flex>

      {dialog && (
        <MachineServiceLogEditor
          key={dialog.key}
          machine={props.machine}
          model={dialog.model}
          onClose={async (reload) => {
            try {
              // just close the dialog
              if (!reload) {
                return;
              }

              // reload logs (e.g. because something changed)
              const nextLogs =
                await MachineServiceLogsService.getInstance().getLatest(
                  props.machine.machineID
                );

              if (!nextLogs) {
                throw new Error('Failed to reload logs');
              }

              setServiceLogs(nextLogs);

              // automatically fix the machine's last_hw_changed date based on results
              const maxResetDate = max(
                nextLogs
                  ?.filter((m) => m.trainingReset)
                  .map((m) => parseISO(m.serviced))
              )?.toISOString();

              if (!maxResetDate) {
                console.debug(
                  'There is no service log that would result in a change to last HW changed date'
                );
                return;
              }

              const shouldUpdate =
                !props.machine.last_hardware_changed ||
                props.machine.last_hardware_changed !== maxResetDate;

              if (!shouldUpdate) {
                console.debug(
                  'There is nothing to update for the last HW changed date'
                );
                return;
              }

              const updated = await updateMachines([
                {
                  _id: props.machine._id,
                  last_hardware_changed: maxResetDate,
                },
              ]);

              // prompt the user to refresh if they should be connected to the machine whose last HW changed date has been updated
              if (updated && current.machineID === props.machine.machineID) {
                NotifyHelper.info({
                  icon: <UpdateIcon />,
                  message_md: [
                    '### Refresh Required',
                    'Last hardware changed date has been updated and training data will adjust accordingly.',
                    'Refresh the app for changes to take effect.',
                  ].join('\n\n'),
                  delay_ms: 0,
                  buttons: [
                    {
                      label: 'Refresh App',
                      onClick: () => window.location.reload(),
                      dismissAfterClick: true,
                    },
                  ],
                });
              }
            } catch (e) {
              console.error(e);
            } finally {
              setDialog(undefined);
            }
          }}
        />
      )}
    </ErrorBoundary>
  );
};
